import { ActiveElement, Chart, Chart as ChartJS, LineElement, LinearScale, PointElement, TimeScale } from "chart.js";
import "chartjs-adapter-date-fns";
import { addDays, format, isBefore, isSameDay, isValid, parse, parseISO, startOfDay, startOfToday } from "date-fns";
import React, { MutableRefObject, useEffect, useMemo, useRef } from "react";
import { Line, getElementAtEvent } from "react-chartjs-2";
import { useParams } from "react-router-dom";
import { useHover } from "usehooks-ts";

//services
import { useGoalDetails, useGoalHistory } from "../../../../../../api/hooks";
import usePageWidthBreakpoint from "../../../../../../hooks/usePageWidthBreakpoint";
//hooks
import useSearchQueryParams from "../../../../../../hooks/useSearchQueryParams";

import { Goal } from "../../../../../../api/services";

import { getColor, stringToColor } from "../../../../../../utils/getColor";
//utils
import { getGoalProgressHistoryData, getProgressLabels, getProjectedValueToToday } from "../utils";

//config
import Config from "../../../config";
//styles
import css from "../SingleGoalInfo.module.scss";

ChartJS.register(TimeScale, LinearScale, LineElement, PointElement);

const GoalProgressLineChart = () => {
	const { isMobile } = usePageWidthBreakpoint();
	const { goal_id: goalId } = useParams<{ goal_id: string }>();
	const { data: goalData } = useGoalDetails({ goalID: goalId || "" });
	const goal = goalData ? goalData.goal : ({} as Goal);

	const { data: goalProgressHistory = [] } = useGoalHistory({
		goalID: goalId || "",
		skip: 0,
		take: 100,
		type: "progress",
		sortDirection: "desc",
		sortName: "as_of",
	});

	const { handleQueryChange } = useSearchQueryParams({
		schema: Config.queryParametersSchema,
	});

	let color = goal?.color?.color || "#3E90C1";
	if (color[0] !== "#") {
		color = stringToColor(color);
	}

	const { end_date, start_date } = goal;

	// formattedData
	const startDate = start_date;
	const endDate = end_date;
	const progressHistory = goalProgressHistory?.filter((history) => history?.change?.value) || [];
	const progressHistoryData = getGoalProgressHistoryData(startDate, progressHistory);
	const isDataBeforeStartDate =
		progressHistoryData.length > 0 && isBefore(parseISO(progressHistoryData[0]?.label), startDate);
	const projectedLineValues =
		isValid(startDate) && endDate && isValid(endDate)
			? getProjectedValueToToday(
					startDate,
					endDate,
					goal?.target,
					isDataBeforeStartDate ? parseISO(progressHistoryData[0]?.label) : undefined
				)
			: [];

	//refs
	const openButtonRef = useRef<HTMLCanvasElement | null>(null);
	const chartRef = useRef<ChartJS>();
	const chartWrapperRef = useRef<HTMLDivElement>(null);

	//flags
	const isEndDateAfterToday = endDate ? isSameDay(endDate, new Date()) || endDate > new Date() : false;
	const isHovered = useHover(chartWrapperRef);

	//memo
	const timeUnit = useMemo(() => {
		const secondsIn6Months = 60 * 60 * 24 * 30 * 6;
		const firstDateInDataSet = parseISO(progressHistoryData[0]?.label);
		const firstDateInProjectedDataSet = parseISO(projectedLineValues[0]?.label);
		const lastDateInDataSet = parseISO(progressHistoryData[progressHistoryData.length - 1]?.label);
		const lastDateInProjectedDataSet = parseISO(projectedLineValues[projectedLineValues.length - 1]?.label);

		const trueFirstDate =
			firstDateInDataSet < firstDateInProjectedDataSet ? firstDateInDataSet : firstDateInProjectedDataSet;
		const trueLastDate =
			lastDateInDataSet > lastDateInProjectedDataSet ? lastDateInDataSet : lastDateInProjectedDataSet;
		const secondsBetweenDates = (trueLastDate?.getTime() - trueFirstDate.getTime()) / 1000;

		return secondsBetweenDates > secondsIn6Months
			? { unit: "month" as const, format: "MMM" }
			: { unit: "day" as const };
	}, [JSON.stringify(goal), JSON.stringify(progressHistoryData)]);

	//functions
	const isPointClickable = (dataSetIndex = 1, element) => {
		const elementDate = startOfDay(parse(element?.raw?.x, "yyyy-MM-dd", new Date()));
		const todaysDate = startOfDay(new Date());

		return isSameDay(elementDate, todaysDate) && dataSetIndex === 1 && goal?.status === "active";
	};

	const handleClick = (event) => {
		const { current: chart } = chartRef;
		const elements = chart ? getElementAtEvent(chart, event) : [];

		if (
			elements &&
			elements.length > 0 &&
			isPointClickable(elements[0].datasetIndex, elements[0]?.element?.["$context"])
		) {
			const initialValue = projectedLineValues[isDataBeforeStartDate ? 2 : 1]?.value?.toFixed(2);
			initialValue &&
				handleQueryChange({ modal: "updateProgress", initialGoalProgressValue: parseFloat(initialValue) });
		}
	};

	useEffect(() => {
		// draw a canvas element that's a green circle with a white cross inside
		const canvas = document.createElement("canvas");

		canvas.width = 10;
		canvas.height = 10;
		const ctx = canvas.getContext("2d");
		if (ctx) {
			ctx.beginPath();
			ctx.arc(5, 5, 5, 0, 2 * Math.PI);
			ctx.fillStyle = "#53A93F";
			ctx.fill();
			ctx.closePath();
			ctx.beginPath();
			ctx.moveTo(5, 2);
			ctx.lineTo(5, 8);
			ctx.moveTo(2, 5);
			ctx.lineTo(8, 5);
			ctx.lineWidth = 2;
			ctx.strokeStyle = "white";
			ctx.stroke();
			ctx.closePath();
		}

		// set the canvas as the point style
		openButtonRef.current = canvas;

		return () => {
			openButtonRef.current = null;
		};
	}, []);

	const data = {
		datasets: [
			{
				label: "Progress",
				clip: 8,
				data: progressHistoryData.map((item) => ({
					x: item.label,
					y: item.value,
				})),
				borderColor: color === "#FFFFFF" ? "#B7BBC2" : color,
				pointBackgroundColor: "white",
				backgroundColor: (context) => {
					const backgroundColor = [`${color}99`, `${color}70`, `${color}60`, `${color}30`, `${color}10`];
					const backupBackgroundColor = ["#00000099", "#00000070", "#00000060", "#00000030", "#00000010"];

					if (!context?.chart?.chartArea || !context?.chart?.ctx || !context?.chart?.data?.datasets) {
						return;
					}

					const { ctx, chartArea } = context.chart;
					const { top, bottom } = chartArea;
					let gradient = ctx.createLinearGradient(0, top, 0, bottom);
					try {
						const colorTranch = 1 / (backgroundColor.length - 1);
						backgroundColor.forEach((color, index) => {
							gradient.addColorStop(0 + index * colorTranch, color);
						});
					} catch {
						gradient = ctx.createLinearGradient(0, top, 0, bottom);
						const colorTranch = 1 / (backupBackgroundColor.length - 1);
						backupBackgroundColor.forEach((color, index) => {
							gradient.addColorStop(0 + index * colorTranch, color);
						});
					}

					return gradient;
				},
				borderWidth: 1.5,
				fill: true,
			},
			{
				label: "Projected",
				clip: 8,
				data: projectedLineValues.map((item) => ({
					x: item.label,
					y: item.value,
				})),
				borderColor: "#B7BBC2",
				backgroundColor: "white",
				borderDash: [12, 8],
				borderWidth: 1.5,
				pointStyle: (value) => {
					return isPointClickable(1, value) && isHovered && goal?.status === "active"
						? openButtonRef.current
						: "circle";
				},
			},
		],
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		spanGaps: true,
		onHover: (_event, elements: ActiveElement[]) => {
			const { current: chart } = chartRef;
			if (
				elements &&
				elements.length > 0 &&
				isPointClickable(elements[0].datasetIndex, elements[0]?.element["$context"]) &&
				chart
			) {
				chart.canvas.style.cursor = "pointer";
			} else if (chart) {
				chart.canvas.style.cursor = "default";
			}
		},
		animations: {
			y: { duration: 0.2 },
		},
		scales: {
			x: {
				suggestedMax: addDays(startOfDay(new Date()), 1),
				suggestedMin: startOfDay(goal?.start_date),
				type: "time" as const,
				min:
					timeUnit.unit === "day"
						? null
						: isDataBeforeStartDate
							? parseISO(progressHistoryData[0]?.label)
							: startDate,
				max:
					timeUnit?.unit === "day"
						? null
						: endDate && isBefore(endDate, startOfToday())
							? startOfToday()
							: endDate,
				time: {
					unit: timeUnit.unit,
					displayFormats: {
						month: timeUnit?.format,
					},
				},
				ticks: {
					offset: true,
					padding: 8,
					font: {
						size: 12,
						family: "Source Sans Pro, sans-serif",
					},
					maxTicksLimit: timeUnit?.unit === "day" ? 10 : 12,
					major: {
						enabled: true,
					},
				},
				grid: {
					drawTicks: false,
					color: getColor("neutral", 2),
					lineWidth: 1,
				},
				border: {
					color: getColor("neutral", 2),
				},
			},
			y: {
				display: true,
				beginAtZero: true,
				suggestedMax: goal?.target,
				ticks: {
					stepSize: 1,
					padding: 8,
					font: {
						size: 12,
						family: "Source Sans Pro, sans-serif",
					},
					beginAtZero: true,
					maxTicksLimit: 20,
				},
				grid: {
					drawTicks: false,
					color: getColor("neutral", 2),
					lineWidth: 1,
				},
				border: {
					color: getColor("neutral", 2),
				},
			},
		},
		plugins: {
			tooltip: {
				displayColors: false,
				legend: {
					display: false,
				},
				callbacks: {
					title: (tooltipItem) => {
						return `${(tooltipItem[0].label = format(parseISO(tooltipItem[0].raw?.x), "MMM, dd yyyy"))} ${
							endDate && isSameDay(parseISO(tooltipItem[0].raw?.x), endDate) ? "(Due Date)" : ""
						} ${isSameDay(parseISO(tooltipItem[0].raw?.x), startDate) ? "(Start Date)" : ""}`;
					},
					label: (tooltipItem) => {
						const labels = getProgressLabels({
							value: Number(tooltipItem?.raw?.y || 0),
							units: goal?.units,
							decimalPlacesOverride: 2,
						});
						//truncate subtext to 40 characters
						if (labels.subText?.length && labels.subText.length > 40) {
							return `${labels.mainText} ${labels.subText.slice(0, 25)}...`;
						}
						return `${labels.mainText} ${labels.subText} ${
							tooltipItem?.datasetIndex === 1 ? "(Projected)" : ""
						}`;
					},
				},
			},
		},
	};

	const dueDateLine = {
		id: "dueDateLine",
		beforeDatasetsDraw: (chart: ChartJS<"line", any, unknown>) => {
			const {
				ctx,
				chartArea: { top, bottom },
				scales: { x },
			} = chart;

			if (!isEndDateAfterToday && endDate) {
				ctx.save();

				//draw line
				ctx.lineWidth = 1.5;
				ctx.strokeStyle = "#B7BBC2";
				const xpos = x.getPixelForValue(endDate?.getTime());
				ctx.beginPath();
				ctx.moveTo(xpos, top);
				ctx.setLineDash([12, 8]);
				ctx.lineTo(xpos, bottom);
				ctx.stroke();
				ctx.closePath();

				ctx.restore();
			}
		},
		afterDatasetsDraw: (chart: ChartJS<"line", any, unknown>) => {
			const {
				ctx,
				chartArea: { top, height },
				scales: { x },
			} = chart;

			if (!isEndDateAfterToday && endDate) {
				ctx.save();
				const xpos = x.getPixelForValue(endDate.getTime());

				// write due date as a vertical label
				ctx.fillStyle = "black";
				ctx.textAlign = "center";
				ctx.textBaseline = "middle";
				ctx.font = "8px Source Sans Pro";
				ctx.translate(0, 0);
				ctx.rotate(Math.PI / 2);
				const heightSplit = height < 100 ? 2 : 5;
				ctx.fillText("Due Date", top + height / heightSplit, -xpos - 6);

				ctx.restore();
			}
		},
	};

	return (
		<div className={`${css.lineChartCard} ${isMobile ? css.mobile : ""}`}>
			<div className={css.lineChartCardInner} ref={chartWrapperRef} data-testid="goal-progress-line-chart">
				<div className={css.lineChart}>
					<Line
						data={data as any}
						options={options as any}
						onClick={handleClick}
						ref={chartRef as MutableRefObject<Chart<"line", any, unknown>>}
						plugins={[dueDateLine]}
					/>
				</div>
			</div>
		</div>
	);
};

export default GoalProgressLineChart;
